/**
 * Inline status select for proposal detail — visually mimics a Badge so the
 * page header reads as "Simulação X [Ganho ▾]" but the badge is clickable
 * and opens a Radix Select.
 *
 * Why not reuse `<StatusBadge>` + a separate Select? Two reasons:
 *   1) The badge IS the trigger — no extra space taken in the header
 *   2) Status changes are common enough during a deal lifecycle that a
 *      single-click affordance matters
 *
 * The actual `actions/solar_change_status` POST is done by the caller via
 * `onChange` — this component just owns the visual + selection UX. Caller
 * also decides what to do on the "ganho" transition (e.g. open ODOO popup).
 */

import { ChevronDown, Loader2 } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-muted text-muted-foreground border-border",
  under_review:
    "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
  sent: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-900",
  ganho:
    "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-900",
  perdido:
    "bg-red-100 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900",
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  under_review: "Em revisão",
  sent: "Enviada",
  ganho: "Ganho",
  perdido: "Perdido",
}

const STATUS_ORDER = ["draft", "under_review", "sent", "ganho", "perdido"] as const

export interface StatusDropdownProps {
  value: string
  onChange: (next: string) => void | Promise<void>
  /** While true, shows a spinner and blocks the select (caller is mid-flight). */
  loading?: boolean
  /** Disables the select but doesn't show a spinner — read-only mode. */
  disabled?: boolean
}

export function StatusDropdown({
  value,
  onChange,
  loading,
  disabled,
}: StatusDropdownProps) {
  const style = STATUS_STYLES[value] ?? STATUS_STYLES.draft
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v !== value) onChange(v)
      }}
      disabled={disabled || loading}
    >
      <SelectTrigger
        className={cn(
          // Reset the default trigger chrome; we want a badge look.
          "inline-flex h-auto w-auto items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-60",
          style,
        )}
        // Hide the native trigger's built-in chevron — we provide our own.
        hideDefaultIcon
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <span>{STATUS_LABELS[value] ?? value}</span>
        )}
        {!loading && <ChevronDown className="h-3 w-3 opacity-70" />}
      </SelectTrigger>
      <SelectContent align="start">
        {STATUS_ORDER.map((s) => (
          <SelectItem key={s} value={s}>
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-block h-2 w-2 rounded-full",
                  STATUS_STYLES[s]?.split(" ")[0] ?? "bg-muted",
                )}
              />
              {STATUS_LABELS[s]}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
